/**
 * AfterCare -- Retrieval-Augmented Generation (RAG) pipeline.
 *
 * Unified RVF/HNSW vector search across ALL knowledge:
 *   1. Drug records           (IDs < 8000000)
 *   2. EMR patient data       (IDs 8000001+ with type: 'emr')
 *   3. Condition knowledge    (IDs 8000001+ with type: 'knowledge')
 *   4. Patient safety data    (allergies, abnormal labs from emr-store — always included)
 *
 * ONE search, ONE vector store, ONE knowledge base — all in RuVector RVF.
 * Everything runs 100% on-device. Zero network calls at runtime.
 */

import { isLLMReady, generateChat } from './llm-engine';
import {
  searchVectors,
  getDrugFromCache,
  getPatientName,
  getPatientMedications,
  getDrugInteractions,
  searchDrugsFromCache,
} from './db';
import {
  getActiveAllergies,
  getAbnormalLabs,
  getActiveDiagnoses,
  getLatestVitals,
  getUpcomingFollowUps,
  getEmrRecord,
} from './emr-store';
import { buildTemplateResponse } from './template-responses';
import { logAction } from './audit-log';
import { t } from '../i18n';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RAGResult {
  /** The generated (or fallback) response text. */
  response: string;
  /** Human-readable source labels for attribution. */
  sources: string[];
  /** True if the response came from the LLM, false if it is a fallback. */
  isLLMGenerated: boolean;
}

interface EmrMetaRecord {
  id: number;
  slug: string;
  type: string;
  section: string;
  generic_name: string;
  description: string;
}

// ---------------------------------------------------------------------------
// EMR metadata cache (loaded once from /emr-metadata.json)
// ---------------------------------------------------------------------------

const EMR_ID_THRESHOLD = 8000000;

let emrMetaCache: Map<string, EmrMetaRecord> | null = null;

async function loadEmrMetadata(): Promise<void> {
  if (emrMetaCache !== null) return;
  emrMetaCache = new Map();
  try {
    const resp = await fetch('/emr-metadata.json');
    if (resp.ok) {
      const records: EmrMetaRecord[] = await resp.json();
      for (const rec of records) {
        emrMetaCache.set(String(rec.id), rec);
      }
      console.log(`[AfterCare] Loaded ${records.length} EMR/knowledge metadata records`);
    }
  } catch (err) {
    console.warn('[AfterCare] Could not load emr-metadata.json:', err);
  }
}

function getEmrMetaFromCache(id: string): EmrMetaRecord | null {
  return emrMetaCache?.get(id) ?? null;
}

function isEmrId(id: string): boolean {
  return Number(id) >= EMR_ID_THRESHOLD;
}

// ---------------------------------------------------------------------------
// Context window construction
// ---------------------------------------------------------------------------

function formatDrugForContext(meta: Record<string, unknown>): string {
  const name = meta.generic_name || 'Unknown';
  const desc = String(meta.description || '').slice(0, 300);
  const sideEffects = Array.isArray(meta.side_effects)
    ? meta.side_effects.slice(0, 5).join(', ')
    : '';
  const dosage = meta.dosage_instructions || '';
  const warnings = Array.isArray(meta.warnings)
    ? meta.warnings.slice(0, 3).join('. ')
    : '';
  const food = Array.isArray(meta.food_interactions)
    ? meta.food_interactions.slice(0, 3).join(', ')
    : '';

  let text = `DRUG: ${name}\n`;
  if (desc) text += `Description: ${desc}\n`;
  if (dosage) text += `Dosage: ${dosage}\n`;
  if (sideEffects) text += `Side effects: ${sideEffects}\n`;
  if (warnings) text += `Warnings: ${warnings}\n`;
  if (food) text += `Food interactions: ${food}\n`;
  return text;
}

function formatEmrForContext(meta: EmrMetaRecord): string {
  const label = meta.type === 'knowledge' ? 'MEDICAL KNOWLEDGE' : 'PATIENT RECORD';
  return `${label} — ${meta.generic_name}:\n${meta.description}\n`;
}

function buildAllergyBlock(): string {
  const allergies = getActiveAllergies();
  if (allergies.length === 0) return '';
  const lines = allergies.map(
    (a) => `- ${a.allergen} (${a.severity}): ${a.reaction}`,
  );
  return `PATIENT ALLERGIES (CRITICAL):\n${lines.join('\n')}\n`;
}

function buildPatientSummary(): string {
  const emr = getEmrRecord();
  const name = getPatientName() || 'Patient';
  const meds = getPatientMedications();

  const parts: string[] = [];
  parts.push(`PATIENT: ${name}`);

  if (emr) {
    const p = emr.patient;
    const age = new Date().getFullYear() - new Date(p.dateOfBirth).getFullYear();
    parts.push(`Age: ${age}, ${p.gender}, ${p.address?.city || ''} ${p.address?.state || ''}`);
    if (emr.hospitalizations.length > 0) {
      const hosp = emr.hospitalizations[emr.hospitalizations.length - 1];
      parts.push(`Recent hospitalization: ${hosp.facility} (${hosp.admissionDate} to ${hosp.dischargeDate})`);
      parts.push(`Admitting diagnosis: ${hosp.admittingDiagnosis}`);
      parts.push(`Discharge disposition: ${hosp.dischargeDisposition}`);
    }
  }

  if (meds.length > 0) {
    const medLines = meds.map(
      (m) => `- ${m.drug_name} (${m.strength}), ${m.frequency.replace(/_/g, ' ')}, times: ${m.times.join(', ')}`,
    );
    parts.push(`\nACTIVE MEDICATIONS (${meds.length}):\n${medLines.join('\n')}`);
  }

  return parts.join('\n');
}

function buildDiagnosisBlock(): string {
  const diagnoses = getActiveDiagnoses();
  if (diagnoses.length === 0) return '';
  const lines = diagnoses.map(
    (d) => `- ${d.description} (${d.icd10Code}) — ${d.status}, onset: ${d.onsetDate}`,
  );
  return `ACTIVE DIAGNOSES:\n${lines.join('\n')}\n`;
}

function buildVitalsBlock(): string {
  const vitals = getLatestVitals();
  if (!vitals) return '';
  const lines: string[] = [];
  if (vitals.systolicBP && vitals.diastolicBP) lines.push(`BP: ${vitals.systolicBP}/${vitals.diastolicBP} mmHg`);
  if (vitals.heartRate) lines.push(`HR: ${vitals.heartRate} bpm`);
  if (vitals.weight) lines.push(`Weight: ${vitals.weight} lbs`);
  if (vitals.bmi) lines.push(`BMI: ${vitals.bmi}`);
  if (vitals.temperature) lines.push(`Temp: ${vitals.temperature}°F`);
  if (vitals.oxygenSaturation) lines.push(`O2 Sat: ${vitals.oxygenSaturation}%`);
  return lines.length > 0 ? `LATEST VITALS: ${lines.join(', ')}\n` : '';
}

function buildFollowUpBlock(): string {
  const followUps = getUpcomingFollowUps();
  if (followUps.length === 0) return '';
  const lines = followUps.map(
    (f) => `- ${f.date}: ${f.provider} (${f.specialty}) — ${f.reason}`,
  );
  return `UPCOMING APPOINTMENTS:\n${lines.join('\n')}\n`;
}

function buildDischargeSummaryBlock(): string {
  const emr = getEmrRecord();
  if (!emr?.dischargeSummary) return '';
  const ds = emr.dischargeSummary;
  const parts: string[] = [];
  parts.push(`Hospital: ${ds.hospitalName} (${ds.admissionDate} to ${ds.dischargeDate})`);
  parts.push(`Attending: ${ds.attendingPhysician}`);
  parts.push(`Admitting diagnosis: ${ds.admittingDiagnosis}`);
  if (ds.hospitalCourse) parts.push(`Course: ${ds.hospitalCourse.slice(0, 600)}`);
  if (ds.dischargeCondition) parts.push(`Condition at discharge: ${ds.dischargeCondition}`);
  if (ds.dischargeInstructions) parts.push(`Instructions: ${ds.dischargeInstructions.slice(0, 400)}`);
  if (ds.warningSignsToWatch.length > 0) parts.push(`Warning signs: ${ds.warningSignsToWatch.join(', ')}`);
  return `DISCHARGE SUMMARY:\n${parts.join('\n')}\n`;
}

function buildCareGoalsBlock(): string {
  const emr = getEmrRecord();
  if (!emr?.carePlan?.goals) return '';
  const goals = emr.carePlan.goals;
  const lines = goals.map(
    (g) => `- ${g.description}: current ${g.currentValue ?? 'N/A'} → target ${g.targetValue} [${g.status}]`,
  );
  return `CARE GOALS:\n${lines.join('\n')}\n`;
}

// ---------------------------------------------------------------------------
// Drug interaction lookup
// ---------------------------------------------------------------------------

/**
 * Extract drug names mentioned in the user's query by matching against
 * the drug cache AND the patient's active medications.
 * Returns an array of { id, name } for each recognised drug.
 */
function extractMentionedDrugs(query: string): { id: string; name: string }[] {
  const lower = query.toLowerCase();
  const found: { id: string; name: string }[] = [];

  // Common OTC drug names that patients frequently ask about.
  const otcNames = [
    'ibuprofen', 'advil', 'motrin',
    'aspirin', 'bayer',
    'acetaminophen', 'tylenol',
    'naproxen', 'aleve',
    'pepto', 'bismol',
    'antacid', 'tums',
  ];

  // Check patient's active medications.
  const meds = getPatientMedications();
  for (const med of meds) {
    const drugNameLower = med.drug_name.toLowerCase();
    // Match on the base drug name (e.g., "metformin" from "Metformin 500mg").
    const baseName = drugNameLower.split(/\s+\d/)[0].trim();
    if (lower.includes(baseName)) {
      found.push({ id: med.drug_rxcui, name: med.drug_name });
    }
  }

  // Check OTC names mentioned in the query.
  for (const otcName of otcNames) {
    if (lower.includes(otcName)) {
      const cacheResults = searchDrugsFromCache(otcName);
      if (cacheResults.length > 0) {
        const first = cacheResults[0];
        found.push({ id: first.id, name: String(first.meta.generic_name || otcName) });
      } else {
        // Even if not in cache, record the mention so interactions can be checked.
        found.push({ id: otcName, name: otcName });
      }
    }
  }

  // Deduplicate by id.
  const seen = new Set<string>();
  return found.filter((d) => {
    if (seen.has(d.id)) return false;
    seen.add(d.id);
    return true;
  });
}

/**
 * Look up all known interactions between the mentioned drugs and the
 * patient's active medications. Returns a formatted context block.
 */
function buildInteractionBlock(query: string): string {
  const mentionedDrugs = extractMentionedDrugs(query);
  if (mentionedDrugs.length === 0) return '';

  const interactions = getDrugInteractions();
  if (interactions.length === 0) return '';

  const patientMeds = getPatientMedications();
  const patientDrugIds = new Set(patientMeds.map((m) => m.drug_rxcui));

  // Collect all drug IDs to check (mentioned + patient's meds).
  const mentionedIds = new Set(mentionedDrugs.map((d) => d.id));

  const relevantInteractions: typeof interactions = [];

  for (const ix of interactions) {
    // An interaction is relevant if one side is a mentioned drug and the
    // other side is either a patient medication or another mentioned drug.
    const aIsMentioned = mentionedIds.has(ix.drug_a_id);
    const bIsMentioned = mentionedIds.has(ix.drug_b_id);
    const aIsPatient = patientDrugIds.has(ix.drug_a_id);
    const bIsPatient = patientDrugIds.has(ix.drug_b_id);

    // Also check by name (interactions.json has drug_a_name/drug_b_name).
    const rec = ix as unknown as Record<string, string>;
    const aNameMentioned = mentionedDrugs.some(
      (d) => d.name.toLowerCase() === (rec.drug_a_name || '').toLowerCase(),
    );
    const bNameMentioned = mentionedDrugs.some(
      (d) => d.name.toLowerCase() === (rec.drug_b_name || '').toLowerCase(),
    );

    const leftMatch = aIsMentioned || aIsPatient || aNameMentioned;
    const rightMatch = bIsMentioned || bIsPatient || bNameMentioned;

    if (leftMatch && rightMatch) {
      relevantInteractions.push(ix);
    }
  }

  if (relevantInteractions.length === 0) return '';

  // Sort by severity: major first.
  const severityOrder: Record<string, number> = { major: 0, moderate: 1, minor: 2 };
  relevantInteractions.sort(
    (a, b) => (severityOrder[a.severity] ?? 3) - (severityOrder[b.severity] ?? 3),
  );

  const lines = relevantInteractions.map((ix) => {
    const rec = ix as unknown as Record<string, string>;
    const drugA = rec.drug_a_name || ix.drug_a_id;
    const drugB = rec.drug_b_name || ix.drug_b_id;
    return `- [${ix.severity.toUpperCase()}] ${drugA} + ${drugB}: ${ix.description} Clinical effect: ${ix.clinical_effect}`;
  });

  return `DRUG INTERACTIONS (SAFETY-CRITICAL):\n${lines.join('\n')}\n`;
}

/**
 * Rough token estimate: ~1 token per 4 characters for English text.
 * Conservative to avoid overflow; Llama tokeniser averages 3.5-4 chars/token.
 */
function estimateTokens(text: string): number {
  return Math.ceil(text.length / 3.5);
}

/**
 * Token-aware context builder.  The n_ctx budget is 8192 tokens.
 * System prompt ≈ 1100, user message ≈ 50-100, nPredict = 512,
 * chat template overhead ≈ 50.  That leaves ~6500 tokens for context.
 * We use 4500 as a safe budget to allow headroom.
 */
const CONTEXT_TOKEN_BUDGET = 4500;

function buildContextWindow(
  drugChunks: { text: string }[],
  emrChunks: { text: string }[],
  knowledgeChunks: { text: string }[],
  query?: string,
): string {
  const parts: string[] = [];
  let usedTokens = 0;

  const addPart = (text: string): boolean => {
    const tokens = estimateTokens(text);
    if (usedTokens + tokens > CONTEXT_TOKEN_BUDGET) return false;
    parts.push(text);
    usedTokens += tokens;
    return true;
  };

  // PRIORITY 1: Patient identity + allergies (safety-critical, always first).
  addPart(buildPatientSummary());

  const allergyBlock = buildAllergyBlock();
  if (allergyBlock) addPart(allergyBlock);

  // PRIORITY 1.5: Drug interactions (safety-critical — after allergies, before everything else).
  if (query) {
    const interactionBlock = buildInteractionBlock(query);
    if (interactionBlock) addPart(interactionBlock);
  }

  // PRIORITY 2: Active diagnoses.
  const diagnosisBlock = buildDiagnosisBlock();
  if (diagnosisBlock) addPart(diagnosisBlock);

  // PRIORITY 2.5: Discharge summary
  const dischargeSummaryBlock = buildDischargeSummaryBlock();
  if (dischargeSummaryBlock) addPart(dischargeSummaryBlock);

  // PRIORITY 3: Abnormal labs.
  const abnormalLabs = getAbnormalLabs();
  if (abnormalLabs.length > 0) {
    const labLines = abnormalLabs.map(
      (l) =>
        `- ${l.testName}: ${l.value} ${l.unit} (ref: ${l.referenceRange}) [${l.interpretation.toUpperCase()}]`,
    );
    addPart(`ABNORMAL LAB RESULTS:\n${labLines.join('\n')}\n`);
  }

  // PRIORITY 4: Vitals.
  const vitalsBlock = buildVitalsBlock();
  if (vitalsBlock) addPart(vitalsBlock);

  // PRIORITY 5: Retrieved search results — add one at a time, best matches first.
  // Knowledge chunks are usually the most relevant to the specific question.
  for (const chunk of knowledgeChunks) {
    if (!addPart(`MEDICAL KNOWLEDGE:\n${chunk.text}`)) break;
  }

  // Drug chunks — medication reference data.
  for (const chunk of drugChunks) {
    if (!addPart(`MEDICATION REFERENCE DATA:\n${chunk.text}`)) break;
  }

  // EMR chunks — patient records.
  for (const chunk of emrChunks) {
    if (!addPart(`RELEVANT MEDICAL RECORDS:\n${chunk.text}`)) break;
  }

  // PRIORITY 6: Care goals + follow-ups (useful but not critical).
  const goalsBlock = buildCareGoalsBlock();
  if (goalsBlock) addPart(goalsBlock);

  const followUpBlock = buildFollowUpBlock();
  if (followUpBlock) addPart(followUpBlock);

  return parts.join('\n---\n');
}

// ---------------------------------------------------------------------------
// Audit hashing
// ---------------------------------------------------------------------------

async function sha256(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ---------------------------------------------------------------------------
// Deterministic emergency detection (NOT model-dependent)
// Research: LLMs fail 50%+ of emergency triage. Hard-code these patterns.
// ---------------------------------------------------------------------------

const EMERGENCY_PATTERNS = [
  /chest\s*(pain|pressure|tight|crush|squeez)/i,
  /can'?t\s*breathe|trouble\s*breathing|short(ness)?\s*of\s*breath|difficulty\s*breathing/i,
  /face\s*(droop|numb|weak)|arm\s*(weak|numb)|slurr(ed|ing)\s*speech/i,
  /severe\s*(headache|head\s*pain)|worst\s*headache/i,
  /swelling.*(face|throat|tongue|lip)|throat.*(clos|swell|tight)/i,
  /uncontrolled\s*bleed|won'?t\s*stop\s*bleeding|bleeding\s*(a\s*lot|heavily)/i,
  /pass(ed)?\s*out|lost\s*consciousness|faint(ed|ing)|confused\s*(and|with)/i,
  /(severe|bad)\s*(stomach|belly|abdominal)\s*pain.*(fever|vomit)/i,
  // Dizziness + rapid/racing heartbeat (either order)
  /\b(?:dizz|lightheaded).*\b(?:heart|pulse).*\b(?:rac|fast|pound|flutter)/i,
  /\b(?:heart|pulse).*\b(?:rac|fast|pound|flutter).*\b(?:dizz|lightheaded)/i,
  // Severe nausea/vomiting (possible lactic acidosis with Metformin)
  /\b(?:severe|constant|won'?t stop)\s+(?:nausea|vomiting|throwing up)/i,
  // Vision changes + headache (hypertensive crisis)
  /\b(?:vision|sight|see).*\b(?:blur|double|loss).*\b(?:headache|head\s+hurt)/i,
  // Dangerously low blood sugar (readings under 50, number must be near sugar/glucose context)
  /\b(?:sugar|glucose|blood\s+sugar)\b.{0,20}\b(?:[1-3]?\d|4[0-9])\b/i,
  // Dangerously high blood sugar (300+, number must be near sugar/glucose context)
  /\b(?:sugar|glucose|blood\s+sugar)\b.{0,20}\b[3-9]\d{2}\b/i,
  // Falls
  /\b(?:i\s+fell|can'?t\s+get\s+up|fell\s+down|falling)\b/i,
  // Severe muscle pain (rhabdomyolysis risk with statins)
  /\b(?:severe|extreme|worst)\s+(?:muscle)\s+(?:pain|ache|cramp)/i,
];

const CRISIS_PATTERNS = [
  /want\s*to\s*(die|end\s*it|kill\s*my|hurt\s*my)/i,
  /suicid(e|al)|self[- ]?harm/i,
  /don'?t\s*(see\s*the\s*point|want\s*to\s*(live|go\s*on|be\s*here))/i,
  /better\s*off\s*(dead|without\s*me)|no\s*reason\s*to\s*live/i,
];

/**
 * CALL_DOCTOR patterns: concerning symptoms that need medical attention
 * but are not immediately life-threatening. The patient should call their
 * doctor's office right away or go to urgent care.
 */
const CALL_DOCTOR_PATTERNS = [
  // Dizziness alone (without other emergency signs like rapid heartbeat)
  /\b(?:dizz|lightheaded)\b/i,
  // New swelling in ankles, feet, legs, or face
  /\b(?:swelling|swollen)\s+(?:ankle|feet|leg|face)/i,
  // Cannot keep food or medication down
  /\b(?:can'?t\s+keep|keeping)\s+(?:food|medicine|medication)\s+down/i,
  // Dark, bloody, or black urine or stool
  /\b(?:dark|bloody|black)\s+(?:urine|stool|pee|poop)/i,
];

function getEmergencyResponse(): string {
  return t('emergency.call911');
}

function getCrisisResponse(): string {
  return t('emergency.crisis');
}

function getCallDoctorResponse(): string {
  return t('emergency.callDoctor');
}

type DetectionResult = { response: string; type: 'crisis' | 'emergency' | 'call_doctor' } | null;

function detectEmergency(question: string): DetectionResult {
  // Check crisis first (highest priority — suicide/self-harm).
  for (const pattern of CRISIS_PATTERNS) {
    if (pattern.test(question)) return { response: getCrisisResponse(), type: 'crisis' };
  }
  // Check emergency patterns (call 911).
  for (const pattern of EMERGENCY_PATTERNS) {
    if (pattern.test(question)) return { response: getEmergencyResponse(), type: 'emergency' };
  }
  // Check call-doctor patterns (urgent but not 911).
  for (const pattern of CALL_DOCTOR_PATTERNS) {
    if (pattern.test(question)) return { response: getCallDoctorResponse(), type: 'call_doctor' };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Run a full RAG query against the unified RVF knowledge base:
 *   1. Search RVF/HNSW for top-10 relevant results (drugs + EMR + knowledge)
 *   2. Separate results by type (drug vs EMR vs knowledge)
 *   3. Build context window with structured EMR data + search results
 *   4. Generate response via on-device LLM
 *   5. Log an auditable record of the query
 *
 * Returns a fallback message if the LLM is not yet loaded.
 */
export async function queryRAG(
  question: string,
  queryVector: Float32Array,
  onToken?: (token: string) => void,
  intentType?: string,
): Promise<RAGResult> {
  // SAFETY FIRST: deterministic emergency detection (bypasses LLM entirely).
  const emergencyResult = detectEmergency(question);
  if (emergencyResult) {
    logAction('emergency_detected', JSON.stringify({
      queryHash: await sha256(question),
      type: emergencyResult.type,
    }));
    return {
      response: emergencyResult.response,
      sources: [],
      isLLMGenerated: false,
    };
  }

  // TEMPLATE TIER: If LLM is not ready, try template responses from structured data.
  if (!isLLMReady() && intentType) {
    const templateResponse = buildTemplateResponse(intentType as any, question);
    if (templateResponse) {
      logAction('template_response', JSON.stringify({
        intent: intentType,
        queryHash: await sha256(question),
      }));
      return {
        response: templateResponse,
        sources: ['Patient medical records'],
        isLLMGenerated: false,
      };
    }
  }

  // Load EMR metadata for lookup (one-time).
  await loadEmrMetadata();

  // 1. UNIFIED RVF SEARCH — one query, all knowledge types.
  // Request more results (top-10) so we get a good mix of drugs + EMR + knowledge.
  const allResults = await searchVectors(queryVector, 15);

  // 2. Separate results by type.
  const drugChunks: { text: string; score: number }[] = [];
  const emrChunks: { text: string; score: number }[] = [];
  const knowledgeChunks: { text: string; score: number }[] = [];

  for (const r of allResults) {
    const score = 1 - r.distance;

    if (isEmrId(r.id)) {
      // EMR or Knowledge record
      const emrMeta = getEmrMetaFromCache(r.id);
      if (emrMeta) {
        const formatted = formatEmrForContext(emrMeta);
        if (emrMeta.type === 'knowledge') {
          knowledgeChunks.push({ text: formatted, score });
        } else {
          emrChunks.push({ text: formatted, score });
        }
      }
    } else {
      // Drug record
      const meta = getDrugFromCache(r.id);
      if (meta) {
        drugChunks.push({
          text: formatDrugForContext(meta),
          score,
        });
      }
    }
  }

  console.log(`[AfterCare RAG] Results: ${drugChunks.length} drugs, ${emrChunks.length} EMR, ${knowledgeChunks.length} knowledge`);

  // 3. Build context window (pass query for drug interaction lookup).
  const context = buildContextWindow(drugChunks, emrChunks, knowledgeChunks, question);

  // 4. Generate with LLM (or return fallback).
  if (!isLLMReady()) {
    return {
      response:
        t('chat.gettingReady'),
      sources: [],
      isLLMGenerated: false,
    };
  }

  try {
    const rawResponse = await generateChat(question, context, onToken);

    // Append medical disclaimer to every LLM response.
    const disclaimer = '\n\n' + t('chat.disclaimer');
    const response = rawResponse + disclaimer;

    // 5. Audit trail.
    const sources = [
      ...drugChunks.map((c) => c.text.split('\n')[0] || ''),
      ...emrChunks.map((c) => c.text.split('\n')[0] || ''),
      ...knowledgeChunks.map((c) => c.text.split('\n')[0] || ''),
    ];
    const responseHash = await sha256(rawResponse);
    logAction(
      'llm_query',
      JSON.stringify({
        query: question.slice(0, 200),
        sourceCount: sources.length,
        drugSources: drugChunks.length,
        emrSources: emrChunks.length,
        knowledgeSources: knowledgeChunks.length,
        responseHash,
      }),
    );

    // Log EMR access for HIPAA compliance.
    if (emrChunks.length > 0 || knowledgeChunks.length > 0) {
      logAction('emr_access', JSON.stringify({
        sections: [...emrChunks, ...knowledgeChunks].map((c) => c.text.slice(0, 60)),
        queryHash: await sha256(question),
      }));
    }

    return { response, sources, isLLMGenerated: true };
  } catch (err) {
    console.error('[AfterCare] RAG generation failed:', err);
    return {
      response: t('chat.error'),
      sources: [],
      isLLMGenerated: false,
    };
  }
}
